/**
 * Templates (CFO plan 4.2). A project template is the project type, extended (projecttypes.js: jobType, tags,
 * taskTemplates refs, payDefault, metrics, estimateMap). A task template is its own doc, `taskTemplates/{id}`
 * (opsTaskTemplates — owners write, everyone reads):
 *
 *   { title, tags: [], departmentId, estimate: { minutes, minutesPerUnit, unit }, piecePrice: { amount, perUnit } | null,
 *     metrics: [{ metricId, source: 'auto' | 'quote' | 'ask' }], materials: [{ name, qty?, unit? }],
 *     photos: { before, after }, approval: 'none' | 'review', catalogItemIds: [],
 *     learned: { minutesPerUnit, samples, updatedAt } | null,
 *     list?: { templateId?, name?, items: [{ text, qty? }] } }   — a list the task starts with (shared/lists.js)
 *
 * `learned` is written by the job post-mortem (4.5) once Lj approves it. Pure — no I/O.
 */
import { withTag } from './tags.js';
import { cloneProjectType, makeProjectType } from './projecttypes.js';
import { JOB_TYPES } from './profit.js';
import { STARTER_METRICS, makeMetric } from './metrics.js';
import { DEPARTMENT_DEFAULTS, GENERAL_DEPARTMENT_ID } from './hierarchy.js';
import { FIELD_QUICK_TASKS, quickTasksFor, cleanQuickTasks } from './quicktasks.js';
import { cleanTemplateList, listForTemplate } from './lists.js';

export const TEMPLATE_APPROVALS = ['none', 'review'];
export const METRIC_SOURCES = ['auto', 'quote', 'ask'];
/** BattleBids line categories — what a type's estimate map can match on besides a price-book item. */
export const BID_CATEGORIES = ['labor', 'materials', 'equipment', 'subcontractor', 'permits', 'other'];

const num = (v) => (v === null || v === undefined || v === '' ? null : Number.isFinite(Number(v)) && Number(v) > 0 ? Number(v) : null);
const round2 = (n) => Math.round(n * 100) / 100;
const str = (v) => String(v ?? '').trim();
const tagsOf = (list) => (Array.isArray(list) ? list : []).reduce((acc, t) => withTag(acc, t), []);
const key = (t) => str(t).toLowerCase().replace(/\s+/g, ' ');

/** A clean task template (unknown fields dropped; id / createdAt / updatedAt / createdFrom kept). */
export function makeTaskTemplate(o = {}) {
  const est = o.estimate || {};
  const pp = o.piecePrice || {};
  const piece = num(pp.amount) || num(pp.perUnit) ? { amount: num(pp.amount), perUnit: num(pp.perUnit) } : null;
  const list = cleanTemplateList(o.list);
  const learned = o.learned && num(o.learned.minutesPerUnit)
    ? { minutesPerUnit: num(o.learned.minutesPerUnit), samples: Math.max(0, Math.round(Number(o.learned.samples) || 0)), updatedAt: o.learned.updatedAt || null } : null;
  return {
    ...(o.id ? { id: o.id } : {}),
    title: str(o.title),
    tags: tagsOf(o.tags),
    departmentId: o.departmentId || null,
    estimate: { minutes: num(est.minutes), minutesPerUnit: num(est.minutesPerUnit), unit: str(est.unit) || null },
    piecePrice: piece,
    metrics: (Array.isArray(o.metrics) ? o.metrics : [])
      .map((m) => (typeof m === 'string' ? { metricId: m, source: 'auto' } : m))
      .filter((m) => m && str(m.metricId))
      .map((m) => ({ metricId: str(m.metricId), source: METRIC_SOURCES.includes(m.source) ? m.source : 'auto' })),
    materials: (Array.isArray(o.materials) ? o.materials : [])
      .map((m) => (typeof m === 'string' ? { name: m } : m))
      .filter((m) => m && str(m.name))
      .map((m) => ({ name: str(m.name), ...(num(m.qty) ? { qty: num(m.qty) } : {}), ...(str(m.unit) ? { unit: str(m.unit) } : {}) })),
    photos: { before: !!o.photos?.before, after: !!o.photos?.after },
    approval: TEMPLATE_APPROVALS.includes(o.approval) ? o.approval : 'none',
    catalogItemIds: [...new Set((Array.isArray(o.catalogItemIds) ? o.catalogItemIds : []).map(str).filter(Boolean))],
    learned,
    ...(list ? { list } : {}),
    ...(o.createdFrom ? { createdFrom: o.createdFrom } : {}),
    ...(o.createdAt ? { createdAt: o.createdAt } : {}),
    ...(o.updatedAt ? { updatedAt: o.updatedAt } : {}),
  };
}

/** Minutes per unit to plan with: what real jobs taught it (learned, once it has samples), else the one set by hand. */
export function templateMinutesPerUnit(tpl) {
  if (tpl?.learned && num(tpl.learned.minutesPerUnit) && Number(tpl.learned.samples) > 0) return num(tpl.learned.minutesPerUnit);
  return num(tpl?.estimate?.minutesPerUnit);
}

/**
 * A task from a template, as addTask / makeTaskDoc input (no id).
 *   estimate = minutes per unit × qty, else the template's minutes;
 *   piece price = per unit × qty, else its amount (a suggestion on the task — `piecePrice`; the piece itself needs who);
 *   tags, photo rules (after photo = requiresAfterPhoto), review, metrics copied; templateId remembered;
 *   its list copied (from its list template as it is now, when `listTemplates` has it — shared/lists.js).
 */
export function makeTaskFromTemplate(tpl, { qty = null, unit = null, projectId = null, listTemplates = [] } = {}) {
  const t = makeTaskTemplate(tpl);
  if (!t.title) throw new Error('the template has no title');
  const q = num(qty);
  const mpu = templateMinutesPerUnit(t);
  const minutes = q && mpu ? Math.round(mpu * q) : t.estimate.minutes;
  const piece = t.piecePrice ? (q && t.piecePrice.perUnit ? round2(t.piecePrice.perUnit * q) : t.piecePrice.amount) : null;
  const u = str(unit) || t.estimate.unit;
  const list = listForTemplate(t.list, listTemplates || []);
  return {
    title: t.title,
    projectId: projectId || null,
    tags: t.tags,
    estimatedMinutes: minutes || null,
    templateId: tpl?.id || null,
    ...(q ? { qty: q } : {}),
    ...(u && (q || t.estimate.unit) ? { unit: u } : {}),
    ...(t.departmentId ? { departmentId: t.departmentId } : {}),
    ...(piece ? { piecePrice: piece } : {}),
    requiresAfterPhoto: t.photos.after,
    ...(t.photos.before ? { requiresBeforePhoto: true } : {}),
    ...(t.approval === 'review' ? { approval: 'review' } : {}),
    ...(t.metrics.length ? { metrics: t.metrics } : {}),
    ...(list ? { list } : {}),
  };
}

/** A type's task-template refs, clean: [{ templateId, phase }] (a bare id = no phase). */
export function typeTemplateRefs(type) {
  return (Array.isArray(type?.taskTemplates) ? type.taskTemplates : [])
    .map((r) => (typeof r === 'string' ? { templateId: r } : r))
    .filter((r) => r && str(r.templateId))
    .map((r) => ({ templateId: str(r.templateId), phase: str(r.phase) || null, ...(num(r.qty) ? { qty: num(r.qty) } : {}) }));
}

/** A type's estimate map, clean: [{ catalogItemId?, category?, templateId }]. */
export function typeEstimateMap(type) {
  return (Array.isArray(type?.estimateMap) ? type.estimateMap : [])
    .filter((m) => m && str(m.templateId) && (str(m.catalogItemId) || str(m.category)))
    .map((m) => ({ ...(str(m.catalogItemId) ? { catalogItemId: str(m.catalogItemId) } : {}), ...(str(m.category) ? { category: str(m.category).toLowerCase() } : {}), templateId: str(m.templateId) }));
}

const taskMs = (t) => {
  const e = Array.isArray(t.timeEntries) ? t.timeEntries : [];
  const sum = e.reduce((s, x) => s + (x?.duration || 0), 0);
  return sum || t.timeSpent || 0;
};
const isWork = (t) => t && !t.bucket && !t.runId && !t.quickTaskId && !t.parentId && !t.deletedAt;

/**
 * "Save as template": a type built from a project — its sections as phases, its tasks as task templates (actual
 * minutes as the estimate, per unit when the task has a quantity), its tags and job type. A task that came from a
 * template (templateId), or whose title matches one, reuses that template instead of making another.
 * Starts from the project's own type when it has one (checklists, capture). Nothing is written here.
 * @returns {{ type, templates: Array<template & { id, isNew }> }}
 */
export function projectToTemplate({ project, sections = [], tasks = [], templates = [], types = [], newId, name = null }) {
  if (!project) throw new Error('no project');
  const base = project.typeId ? types.find((t) => t.id === project.typeId) : null;
  const secs = sections.filter((s) => s.projectId === project.id).sort((a, b) => (a.order || 0) - (b.order || 0));
  const secTitle = new Map(secs.map((s) => [s.id, str(s.title)]));
  const byId = new Map(templates.map((t) => [t.id, t]));
  const byTitle = new Map(templates.map((t) => [key(t.title), t]));
  // The closing phase's steps come back with the closing phase itself — not as task templates too.
  const closing = new Set((base?.closingPhase?.steps || []).map((x) => key(typeof x === 'string' ? x : x?.title)));
  const secIdx = (t) => { const i = secs.findIndex((s) => s.id === t.sectionId); return i < 0 ? secs.length : i; };   // no phase → last
  const mine = tasks.filter((t) => t.projectId === project.id && isWork(t) && str(t.title) && !closing.has(key(t.title)))
    .sort((a, b) => (secIdx(a) - secIdx(b)) || (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  const out = [];
  const refs = [];
  const made = new Map();
  for (const t of mine) {
    const phase = t.sectionId ? secTitle.get(t.sectionId) || null : null;
    const have = (t.templateId && byId.get(t.templateId)) || byTitle.get(key(t.title)) || made.get(key(t.title));
    if (have) {
      if (!out.some((x) => x.id === have.id)) out.push({ ...makeTaskTemplate(have), id: have.id, isNew: !!have.isNew });
      if (!refs.some((r) => r.templateId === have.id && r.phase === phase)) refs.push({ templateId: have.id, phase });
      continue;
    }
    const actual = Math.round(taskMs(t) / 60000);
    const minutes = actual > 0 ? actual : num(t.estimatedMinutes);
    const q = num(t.qty);
    const tpl = {
      ...makeTaskTemplate({
        title: t.title, tags: t.tags, departmentId: t.departmentId || null,
        estimate: { minutes: q ? null : minutes, minutesPerUnit: q && minutes ? round2(minutes / q) : null, unit: q ? t.unit || null : null },
        piecePrice: t.piecePrice ? (q ? { perUnit: round2(Number(t.piecePrice) / q) } : { amount: t.piecePrice }) : null,
        photos: { before: !!t.requiresBeforePhoto, after: !!t.requiresAfterPhoto },
        approval: t.approval === 'review' ? 'review' : 'none',
        metrics: t.metrics || [],
        catalogItemIds: t.catalogItemId ? [t.catalogItemId] : [],
        createdFrom: { projectId: project.id, taskId: t.id },
      }),
      id: newId(),
      isNew: true,
    };
    made.set(key(t.title), tpl);
    out.push(tpl);
    refs.push({ templateId: tpl.id, phase });
  }
  const start = base ? cloneProjectType(base, base.name) : makeProjectType({ icon: project.icon || '🧱' });
  const type = makeProjectType({
    ...start,
    clonedFrom: base?.id || null,
    name: str(name) || (base ? `${base.name} (from ${project.name})` : project.name),
    departmentId: project.departmentId || start.departmentId || null,
    phases: secs.filter((s) => str(s.title) && str(s.title) !== str(base?.closingPhase?.name)).map((s) => ({ name: str(s.title) })),
    jobType: JOB_TYPES.includes(project.jobType) ? project.jobType : start.jobType || null,
    tags: tagsOf([...(start.tags || []), ...(project.tags || [])]),
    taskTemplates: refs,
    estimateMap: start.estimateMap || [],
  });
  return { type, templates: out };
}

/**
 * Titles done ≥ `min` times with no template behind them — Setup → Templates offers "make this a template".
 * Only work tasks (no checklists, routines or quick tasks). A suggestion only; nothing is written.
 * @returns {Array<{ title, count, projects, avgMinutes, tags }>}
 */
export function suggestTemplates({ tasks = [], templates = [], min = 3 } = {}) {
  const have = new Set(templates.map((t) => key(t.title)));
  const groups = new Map();
  for (const t of tasks) {
    if (!isWork(t) || t.status !== 'done' || t.templateId || !str(t.title)) continue;
    const k = key(t.title);
    if (have.has(k)) continue;
    if (!groups.has(k)) groups.set(k, { title: str(t.title), list: [] });
    groups.get(k).list.push(t);
  }
  const out = [];
  for (const { title, list } of groups.values()) {
    if (list.length < min) continue;
    const timed = list.map(taskMs).filter((ms) => ms > 0);
    const tagCount = new Map();
    list.forEach((t) => (t.tags || []).forEach((g) => tagCount.set(g, (tagCount.get(g) || 0) + 1)));
    out.push({
      title, count: list.length,
      projects: new Set(list.map((t) => t.projectId || '')).size,
      avgMinutes: timed.length ? Math.round(timed.reduce((a, b) => a + b, 0) / timed.length / 60000) : null,
      tags: [...tagCount.entries()].filter(([, n]) => n * 2 >= list.length).map(([g]) => g),
    });
  }
  return out.sort((a, b) => b.count - a.count || a.title.localeCompare(b.title));
}

// ── Starter packs (code constants; written only when someone taps "Add starter pack") ───────────────────────────
// Each: types (project templates) + task templates + tags + metric ids. Estimates and piece prices are left blank on
// purpose — the units are set, and real jobs fill in minutes per unit (learned). Template refs use `key`.
const T = (key, title, unit, extra = {}) => ({ key, title, estimate: { unit }, ...extra });
// CFO 5.6: each pack also brings the standard departments with the industry's quick tasks — Field (the job-site
// stretches on top of Setup / Cleanup / Material run / Travel) and Office (the paperwork, on the job you're on).
const Q = (id, title, tags) => ({ id, title, scope: 'job', ...(tags ? { tags } : {}) });
const field = (...extra) => ({ name: 'Field', icon: '🔨', color: '#E8833A', quickTasks: [...FIELD_QUICK_TASKS.map((q) => ({ ...q })), ...extra] });
const OFFICE = { name: 'Office', icon: '🗂️', color: '#5B8DEF', quickTasks: [Q('quote', 'Quote', ['estimating']), Q('order', 'Order materials', ['materials']), Q('invoice', 'Invoice', ['billing']), Q('call', 'Customer call', ['customer'])] };
export const STARTER_PACKS = Object.freeze([
  {
    id: 'renovation', name: 'Renovation', icon: '🛠️',
    tags: ['renovation', 'demo', 'drywall', 'trim'],
    metrics: ['sqft-per-hour'],
    departments: [field(Q('dump-run', 'Dump run', ['disposal']), Q('site-protect', 'Site protection', ['protection'])), OFFICE],
    templates: [
      T('demo', 'Demo', 'sq ft', { tags: ['demo'], photos: { before: true } }),
      T('hang', 'Hang drywall', 'sq ft', { tags: ['drywall'] }),
      T('tape', 'Tape and mud', 'sq ft', { tags: ['drywall'] }),
      T('base', 'Install baseboard', 'lin ft', { tags: ['trim'] }),
      T('door', 'Hang interior door', 'ea', { tags: ['trim'] }),
      T('punch', 'Punch list', null, { photos: { after: true }, approval: 'review' }),
    ],
    types: [
      { name: 'Renovation', icon: '🛠️', jobType: 'fixed', tags: ['renovation'], metrics: ['sqft-per-hour'],
        phases: [{ name: 'Demo' }, { name: 'Rough-in' }, { name: 'Close-up' }, { name: 'Finish' }],
        setupSteps: [{ title: 'Drop cloths down' }, { title: 'Dust barrier up' }],
        cleanupSteps: [{ title: 'Vacuum' }, { title: 'Tools out of the living space' }],
        closingPhase: { name: 'Walkthrough', steps: [{ title: 'Final walkthrough' }] },
        capture: { beforePhotos: true, phasePhoto: true, discoveryLog: true },
        refs: [['demo', 'Demo'], ['hang', 'Close-up'], ['tape', 'Close-up'], ['base', 'Finish'], ['door', 'Finish'], ['punch', 'Walkthrough']] },
    ],
  },
  {
    id: 'painting', name: 'Painting', icon: '🎨',
    tags: ['painting', 'interior', 'exterior'],
    metrics: ['sqft-per-hour', 'paint-litres'],
    departments: [field(Q('paint-run', 'Paint store run', ['materials']), Q('colour-match', 'Colour match', ['painting'])), OFFICE],
    templates: [
      T('mask', 'Mask and protect', 'room', { tags: ['painting'] }),
      T('patch', 'Patch and sand', 'room', { tags: ['painting'] }),
      T('cut', 'Cut in', 'lin ft', { tags: ['painting'] }),
      T('walls', 'Roll walls', 'sq ft', { tags: ['painting'], photos: { after: true }, metrics: [{ metricId: 'paint-litres', source: 'ask' }] }),
      T('ceiling', 'Paint ceiling', 'sq ft', { tags: ['painting'], photos: { after: true } }),
      T('trim', 'Paint trim', 'lin ft', { tags: ['painting'] }),
      T('doors', 'Paint doors', 'ea', { tags: ['painting'] }),
    ],
    types: [
      { name: 'Interior repaint', icon: '🎨', jobType: 'fixed', tags: ['painting', 'interior'], metrics: ['sqft-per-hour', 'paint-litres'],
        phases: [{ name: 'Prep' }, { name: 'Prime' }, { name: 'Paint' }, { name: 'Touch-up' }],
        setupSteps: [{ title: 'Drop cloths down' }, { title: 'Furniture moved and covered' }],
        cleanupSteps: [{ title: 'Brushes and rollers washed' }, { title: 'Lids on, cans out' }],
        closingPhase: { name: 'Walkthrough', steps: [{ title: 'Walkthrough with the customer' }] },
        capture: { beforePhotos: true, phasePhoto: true, discoveryLog: false },
        refs: [['mask', 'Prep'], ['patch', 'Prep'], ['cut', 'Paint'], ['walls', 'Paint'], ['ceiling', 'Paint'], ['trim', 'Paint'], ['doors', 'Paint']] },
    ],
  },
  {
    id: 'landscaping', name: 'Landscaping', icon: '🌿',
    tags: ['landscaping', 'hardscape', 'softscape', 'maintenance'],
    metrics: ['sqft-per-hour'],
    departments: [field(Q('green-waste', 'Green waste drop', ['disposal']), Q('fuel-up', 'Fuel up equipment', ['fuel'])), OFFICE],
    templates: [
      T('sod', 'Lay sod', 'sq ft', { tags: ['softscape'], photos: { after: true } }),
      T('mulch', 'Mulch beds', 'cu yd', { tags: ['softscape'] }),
      T('plant', 'Plant shrubs', 'ea', { tags: ['softscape'] }),
      T('edge', 'Install edging', 'lin ft', { tags: ['hardscape'] }),
      T('pavers', 'Lay pavers', 'sq ft', { tags: ['hardscape'], photos: { before: true, after: true } }),
      T('mow', 'Mow and trim', 'visit', { tags: ['maintenance'] }),
    ],
    types: [
      { name: 'Landscape install', icon: '🌿', jobType: 'fixed', tags: ['landscaping'], metrics: ['sqft-per-hour'],
        phases: [{ name: 'Layout' }, { name: 'Excavation' }, { name: 'Hardscape' }, { name: 'Softscape' }],
        setupSteps: [{ title: 'Locates checked' }, { title: 'Unload equipment' }],
        cleanupSteps: [{ title: 'Blow off hard surfaces' }, { title: 'Load equipment' }],
        closingPhase: { name: 'Walkthrough', steps: [{ title: 'Watering instructions given' }] },
        capture: { beforePhotos: true, phasePhoto: true, discoveryLog: false },
        refs: [['edge', 'Hardscape'], ['pavers', 'Hardscape'], ['sod', 'Softscape'], ['mulch', 'Softscape'], ['plant', 'Softscape']] },
      { name: 'Maintenance visit', icon: '🌱', jobType: 'hourly', tags: ['landscaping', 'maintenance'], metrics: [],
        phases: [], setupSteps: [], cleanupSteps: [{ title: 'Clippings hauled' }], closingPhase: null,
        capture: { beforePhotos: false, phasePhoto: false, discoveryLog: false },
        refs: [['mow', null], ['mulch', null]] },
    ],
  },
]);

/**
 * What a starter pack would add: its types and task templates, skipping any that already exist by name / title (a
 * type's refs then point at the existing template). Ids from newId. Its metric definitions (STARTER_METRICS, fixed
 * ids) come along unless a definition with that id is already here. Its standard departments (CFO 5.6): one that
 * exists by name gets only the quick tasks it doesn't have (by title); a missing one is made. Templates and types land
 * in `departmentId`, else the pack's Field department. Nothing is written here.
 * @returns {{ types, templates, skipped: { types: string[], templates: string[], departments: string[] }, tags, metrics, metricDefs,
 *   departments: { add: Array<department>, update: Array<{ id, name, quickTasks, added: string[] }> } }}
 */
export function starterPack(packId, { types = [], templates = [], metrics = [], departments = [], newId, now = Date.now(), departmentId = null } = {}) {
  const pack = STARTER_PACKS.find((p) => p.id === packId);
  if (!pack) throw new Error(`no starter pack ${packId}`);
  const tplByTitle = new Map(templates.map((t) => [key(t.title), t]));
  const typeNames = new Set(types.map((t) => key(t.name)));
  const idOf = new Map();
  const newTemplates = [];
  const skipped = { types: [], templates: [], departments: [] };

  const depts = { add: [], update: [] };
  const live = (departments || []).filter((d) => d?.id && d.id !== GENERAL_DEPARTMENT_ID);
  let order = live.reduce((m, d) => (Number.isFinite(Number(d.order)) && Number(d.order) < 1e12 ? Math.max(m, Number(d.order)) : m), -1);
  const deptIdByName = new Map();
  for (const d of pack.departments || []) {
    const have = live.find((x) => key(x.name) === key(d.name));
    if (have) {
      const cur = quickTasksFor(have);
      const titles = new Set(cur.map((q) => key(q.title)));
      const ids = new Set(cur.map((q) => q.id));
      const extra = d.quickTasks.filter((q) => !titles.has(key(q.title))).map((q) => ({ ...q, id: ids.has(q.id) ? `${q.id}-${newId()}` : q.id }));
      deptIdByName.set(key(d.name), have.id);
      if (!extra.length) { skipped.departments.push(d.name); continue; }
      depts.update.push({ id: have.id, name: have.name, quickTasks: cleanQuickTasks([...cur, ...extra], newId), added: extra.map((q) => q.title) });
      continue;
    }
    const id = newId();
    order += 1;
    deptIdByName.set(key(d.name), id);
    depts.add.push({ ...DEPARTMENT_DEFAULTS, id, name: d.name, icon: d.icon, color: d.color, order, quickTasks: cleanQuickTasks(d.quickTasks, newId), starterPack: pack.id, createdAt: now, updatedAt: now });
  }
  const deptId = departmentId || deptIdByName.get('field') || null;

  for (const { key: k, ...t } of pack.templates) {
    const have = tplByTitle.get(key(t.title));
    if (have) { idOf.set(k, have.id); skipped.templates.push(t.title); continue; }
    const tpl = { ...makeTaskTemplate({ ...t, departmentId: deptId }), id: newId(), starterPack: pack.id, createdAt: now, updatedAt: now };
    idOf.set(k, tpl.id);
    newTemplates.push(tpl);
  }
  const newTypes = [];
  for (const { refs, ...t } of pack.types) {
    if (typeNames.has(key(t.name))) { skipped.types.push(t.name); continue; }
    newTypes.push({
      ...makeProjectType({ ...t, departmentId: deptId, taskTemplates: refs.filter(([k]) => idOf.has(k)).map(([k, phase]) => ({ templateId: idOf.get(k), phase })) }),
      id: newId(), starterPack: pack.id, createdAt: now, updatedAt: now,
    });
  }
  const haveMetric = new Set(metrics.map((m) => m.id));
  const metricDefs = pack.metrics.filter((id) => STARTER_METRICS[id] && !haveMetric.has(id))
    .map((id) => ({ ...makeMetric(STARTER_METRICS[id]), id, starterPack: pack.id, createdAt: now, updatedAt: now }));
  return { types: newTypes, templates: newTemplates, skipped, tags: [...pack.tags], metrics: [...pack.metrics], metricDefs, departments: depts };
}

/** The preview lines a confirm shows before a pack is added (web Setup → Templates and "Getting the numbers right"). */
export function starterPackSummary(r) {
  const n = (k, one, many) => `${k} ${k === 1 ? one : many}`;
  return [
    r.departments.add.length ? `Departments: ${r.departments.add.map((d) => `${d.name} (${d.quickTasks.map((q) => q.title).join(', ')})`).join('; ')}` : null,
    ...r.departments.update.map((d) => `${d.name} gets quick tasks: ${d.added.join(', ')}`),
    r.types.length ? `${n(r.types.length, 'project template', 'project templates')}: ${r.types.map((t) => t.name).join(', ')}` : null,
    r.templates.length ? `${n(r.templates.length, 'task template', 'task templates')}: ${r.templates.map((t) => t.title).join(', ')}` : null,
    r.skipped.types.length || r.skipped.templates.length || r.skipped.departments.length ? `Already here, left as they are: ${[...r.skipped.departments, ...r.skipped.types, ...r.skipped.templates].join(', ')}` : null,
    r.metricDefs.length ? `Metrics: ${r.metricDefs.map((m) => m.name).join(', ')}` : null,
    `Tags: ${r.tags.map((t) => `#${t}`).join(' ')}. Units are set; minutes per unit fill in from real jobs.`,
  ].filter(Boolean);
}

/** Nothing new in the pack (all its parts are already here). */
export const starterPackEmpty = (r) => !r.types.length && !r.templates.length && !r.metricDefs.length && !r.departments.add.length && !r.departments.update.length;
